/**
 * useEngagementTracking hook.
 *
 * Attaches listeners for scroll depth, dwell time and common engagement
 * events (WhatsApp/phone/email clicks). Sends lightweight beacons to
 * /api/analytics/track using the enriched event model.
 *
 * Usage: mount once at the app root; it auto-tracks the current pathname.
 * `trackEngagement(action, target, extras)` is exposed globally on window
 * for manual events (form_start, chat_open, etc.).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { getAttributionPayload } from "@/lib/attribution";
import { canTrackAnalytics } from "@/lib/consent";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SESSION_KEY = "sm_sid";  // Shared with PageViewTracker
const MILESTONES = [25, 50, 75, 100];

function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "unknown" + Date.now();
  }
}

async function send(payload) {
  if (!canTrackAnalytics()) return;
  const attr = getAttributionPayload();
  const body = {
    session_id: getSessionId(),
    path: window.location.pathname + window.location.search,
    referrer: document.referrer || null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    screen_width: window.innerWidth,
    visitor_id: attr.visitor_id,
    utm_source: attr.utm_source,
    utm_medium: attr.utm_medium,
    utm_campaign: attr.utm_campaign,
    ...payload,
  };
  // Prefer sendBeacon for unload safety
  try {
    if (payload.event_type === "dwell" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      navigator.sendBeacon(`${API}/analytics/track`, blob);
      return;
    }
    await axios.post(`${API}/analytics/track`, body).catch(() => {});
  } catch {
    /* swallow */
  }
}

// Exposed globally so any component can log engagement without importing.
export function trackEngagement(action, target, extras = {}) {
  return send({ event_type: "click", action, target, ...extras });
}

export default function useEngagementTracking() {
  const { pathname } = useLocation();
  const enteredAtRef = useRef(Date.now());
  const milestonesHitRef = useRef(new Set());
  const scrollTimerRef = useRef(null);

  // Pageview reset — PageViewTracker handles the actual pageview send;
  // we only reset internal counters here.
  useEffect(() => {
    enteredAtRef.current = Date.now();
    milestonesHitRef.current = new Set();
  }, [pathname]);

  // Skip everything on admin / login (their sessions shouldn't pollute stats)
  const skip = pathname.startsWith("/admin") || pathname.startsWith("/login");

  // Scroll depth
  useEffect(() => {
    if (skip) return;
    const onScroll = () => {
      if (scrollTimerRef.current) return;
      scrollTimerRef.current = setTimeout(() => {
        scrollTimerRef.current = null;
        const doc = document.documentElement;
        const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
        const pct = Math.min(100, Math.round(((window.scrollY || 0) / scrollable) * 100));
        for (const m of MILESTONES) {
          if (pct >= m && !milestonesHitRef.current.has(m)) {
            milestonesHitRef.current.add(m);
            send({ event_type: "scroll", scroll_depth: m });
          }
        }
      }, 250);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [pathname, skip]);

  // Dwell time on unload
  useEffect(() => {
    if (skip) return;
    const flushDwell = () => {
      const ms = Date.now() - enteredAtRef.current;
      // Ignore extremely short "bounces" (probably prefetch or navigation)
      if (ms < 1500) return;
      send({ event_type: "dwell", page_time_ms: ms });
    };
    window.addEventListener("beforeunload", flushDwell);
    window.addEventListener("pagehide", flushDwell);
    return () => {
      window.removeEventListener("beforeunload", flushDwell);
      window.removeEventListener("pagehide", flushDwell);
      flushDwell(); // also on route change
    };
  }, [pathname, skip]);

  // Delegated click tracking for common engagement targets
  useEffect(() => {
    if (skip) return;
    const onClick = (e) => {
      const el = e.target.closest?.("a, button");
      if (!el) return;
      const href = el.getAttribute?.("href") || "";
      if (href.startsWith("tel:")) {
        trackEngagement("phone_click", href.replace("tel:", ""));
      } else if (href.startsWith("mailto:")) {
        trackEngagement("email_click", href.replace("mailto:", ""));
      } else if (/wa\.me|whatsapp/i.test(href)) {
        trackEngagement("whatsapp_click", href);
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [pathname, skip]);

  // Expose globally for third-party components (chat widgets, forms)
  useEffect(() => {
    window.__smTrack = trackEngagement;
    return () => {
      delete window.__smTrack;
    };
  }, []);

  return null;
}
